/* eslint-disable complexity */
import { InlineMessage } from '@strata/react-inline-message';
import { PerformanceManagementBase } from '@strata/react-performance';
import { PerformanceStatusIndicator } from '@strata/react-status-indicators';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { getIn } from 'formik';
import { PropTypes } from 'prop-types';

import { getId, isThereAnOnClaimTask } from 'components/valueCase/helpers';
import { PreviewSubtaskDialog } from 'components/valueCase/PreviewSubtaskDialog';

const determineParams = ({
    performanceObject,
    formik,
    subtaskElementId,
    showDefaultSubtaskPreview,
}) => {
    const performanceObjectToUse = performanceObject ?? formik?.values;
    const subtaskToUse = getIn(performanceObjectToUse, 'tasks.0.subtasks.0');
    const showSubtaskDialog = showDefaultSubtaskPreview || subtaskElementId;
    return { performanceObjectToUse, subtaskToUse, showSubtaskDialog };
};

const setSubtaskSelectedOnPreview = (subtaskToUse, setOpenSubTaskPreview, subtaskElementId) => {
    const subtaskId = getId({ namespace: 'subtask', object: subtaskToUse });
    if (subtaskId && !subtaskElementId) setOpenSubTaskPreview(subtaskId);
};

export const PerformancePreview = ({
    formik = undefined,
    subtaskElementId = undefined,
    performanceObject = undefined,
    showDefaultSubtaskPreview = undefined,
    setOpenSubTaskPreview = undefined,
}) => {
    const { performanceObjectToUse, subtaskToUse, showSubtaskDialog } = determineParams({
        performanceObject,
        formik,
        subtaskElementId,
        showDefaultSubtaskPreview,
    });
    if (showDefaultSubtaskPreview && setOpenSubTaskPreview) {
        setSubtaskSelectedOnPreview(subtaskToUse, setOpenSubTaskPreview, subtaskElementId);
    }
    return (
        <div className="sds_performance__preview">
            <div className="font-semi-bold--uppercase padding__bottom--medium">
                Performance Requirement Preview
            </div>
            <PerformanceManagementBase
                performanceStatus={PerformanceStatusIndicator.Statuses.NOT_STARTED}
                incentiveAmount={performanceObjectToUse?.incentive_amt ?? 0}
                performanceName={
                    performanceObjectToUse?.performance_requirement_name ??
                    'Performance Requirement Name'
                }
                performanceDescription={
                    performanceObjectToUse?.value_case_description ??
                    'Description of Performance Requirement'
                }
                performanceDueDate={
                    performanceObjectToUse?.performance_days
                        ? dayjs().add(performanceObjectToUse?.performance_days, 'day').toISOString()
                        : ''
                }
                performanceEvaluationDate={
                    performanceObjectToUse?.performance_days &&
                    performanceObjectToUse?.evaluation_days
                        ? dayjs()
                              .add(performanceObjectToUse?.performance_days, 'day')
                              .add(performanceObjectToUse?.evaluation_days, 'day')
                              ?.toISOString()
                        : ''
                }
                tasksData={performanceObjectToUse?.tasks}
            />
            {formik?.values?.tasks && isThereAnOnClaimTask(formik.values.tasks) && (
                <div className="padding__top--medium">
                    <InlineMessage
                        label="This performance item contains a task that will duplicate for every claim that is received."
                        intent={InlineMessage.Intent.INFO}
                        wrapText
                    />
                </div>
            )}
            <div className={classNames('font-semi-bold--uppercase', 'padding__top--large')}>
                Subtask Dialog Preview
            </div>
            {showSubtaskDialog ? (
                <PreviewSubtaskDialog
                    elementId={subtaskElementId}
                    subtaskSource={performanceObjectToUse}
                    subtaskObject={subtaskToUse}
                />
            ) : (
                <div className="sds_performance__preview__select-subtask-text">
                    Select a subtask to see a preview
                </div>
            )}
        </div>
    );
};

PerformancePreview.propTypes = {
    formik: PropTypes.shape({
        values: PropTypes.shape({ tasks: PropTypes.arrayOf(PropTypes.shape({})) }),
    }),
    subtaskElementId: PropTypes.string,
    performanceObject: PropTypes.shape({}),
    showDefaultSubtaskPreview: PropTypes.bool,
    setOpenSubTaskPreview: PropTypes.func,
};
